import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, BookUser, UserCheck, LayoutGrid, Shield, UserPlus, Megaphone, Send,
  Download, Activity, PartyPopper, History, Sparkles, ArrowRight, Clock,
  AlertCircle, Zap, Bell, TrendingUp, Dot, UserX
} from 'lucide-react';
import StaffManagementPageRevamped from './StaffManagementPageRevamped';
import StaffDirectoryPage from './StaffDirectoryPage';
import UserManagementPage from './UserManagementPage';
import { CelebrationsPulse } from '../../components/people/CelebrationsPulse';
import { StaffHRProfile } from '../../components/hr/StaffHRProfile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

/**
 * People Hub — the gateway to everyone who works at or with Starnight.
 * Overview tab lands you with KPIs, celebrations, quick actions and recent activity.
 * Deeper tabs (Staff Management / Directory / User Accounts) are untouched.
 */
const PeopleHubPage = (props) => {
  const canManage = ['admin', 'executive', 'superadmin'].includes(props.currentUser?.role);
  const [tab, setTab] = useState(canManage ? 'overview' : 'directory');
  const [stats, setStats] = useState({ staff: 0, users: 0, online: 0, celebrations: 0, suspended: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [profileUsername, setProfileUsername] = useState(null);

  const fetchStats = async () => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      const [staffRes, usersRes, onlineRes, celebRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/staff-accounts`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/user-accounts`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/people/online-presence?window_minutes=5`, { headers: h }).catch(() => ({ data: { count: 0 } })),
        axios.get(`${BACKEND_URL}/api/admin/people/celebrations`, { headers: h }).catch(() => ({ data: { total: 0 } })),
      ]);
      const staffList = staffRes.data || [];
      setStats({
        staff: staffList.length,
        users: (usersRes.data || []).length,
        online: onlineRes.data?.count || 0,
        celebrations: celebRes.data?.total || 0,
        suspended: staffList.filter(s => s.is_suspended).length,
      });
    } catch {}
  };

  const fetchRecentActivity = async () => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      // Grab the superadmin's recent admin actions as a stand-in "team recent activity" feed
      const myUsername = props.currentUser?.username;
      if (!myUsername) return;
      const r = await axios.get(`${BACKEND_URL}/api/admin/staff-accounts/${myUsername}/action-log?limit=6`, { headers: h });
      setRecentActivity(r.data.entries || []);
    } catch {}
  };

  useEffect(() => {
    if (!canManage) return;
    fetchStats();
    fetchRecentActivity();
    const t = setInterval(fetchStats, 30000);
    return () => clearInterval(t);
  }, [canManage]);

  const tabs = [
    { id: 'overview',  label: 'Overview',          icon: LayoutGrid, visible: canManage, testId: 'hub-tab-overview' },
    { id: 'staff',     label: 'Staff Management',  icon: Users,      visible: canManage, testId: 'hub-tab-staff',   badge: stats.staff },
    { id: 'directory', label: 'Directory',         icon: BookUser,   visible: true,      testId: 'hub-tab-directory' },
    { id: 'users',     label: 'User Accounts',     icon: UserCheck,  visible: canManage, testId: 'hub-tab-users',   badge: stats.users },
  ];

  // Non-managers get the simple directory shell
  if (!canManage) {
    return (
      <div className="space-y-4 pb-6" data-testid="people-hub">
        <PeopleHero stats={stats} online={0} />
        <StaffDirectoryPage {...props} />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6 relative" data-testid="people-hub">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute -top-20 left-1/4 w-[500px] h-[300px] bg-cyan-500/[0.08] rounded-full blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[280px] bg-violet-500/[0.06] rounded-full blur-[120px]" />
      </div>

      {/* HERO */}
      <PeopleHero stats={stats} />

      {/* TAB STRIP (richer) */}
      <div className="flex items-center gap-1.5 p-1.5 rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-x-auto no-scrollbar">
        {tabs.filter(t => t.visible !== false).map(t => {
          const active = tab === t.id;
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={t.testId}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-medium whitespace-nowrap transition-all ${
                active
                  ? 'bg-gradient-to-r from-cyan-500/20 to-violet-500/15 text-white border border-cyan-400/30 shadow-[0_0_20px_-8px_rgba(0,229,255,0.5)]'
                  : 'text-white/55 hover:text-white hover:bg-white/[0.04] border border-transparent'
              }`}>
              {active && <Dot className="w-4 h-4 text-cyan-300 -mx-1" strokeWidth={6} />}
              <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span>{t.label}</span>
              {t.badge != null && t.badge > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[9px] font-bold ${active ? 'bg-cyan-500/30 text-cyan-100' : 'bg-white/[0.06] text-white/50'}`}>
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* CONTENT */}
      {tab === 'overview' && (
        <PeopleOverview
          stats={stats}
          recentActivity={recentActivity}
          onJumpTo={setTab}
          onOpenStaff={(u) => setProfileUsername(u)}
        />
      )}
      {tab === 'staff' && <StaffManagementPageRevamped {...props} />}
      {tab === 'directory' && <StaffDirectoryPage {...props} />}
      {tab === 'users' && <UserManagementPage {...props} />}

      {/* HR profile drawer (from overview quick-link) */}
      {profileUsername && (
        <StaffHRProfile username={profileUsername} onClose={() => setProfileUsername(null)} />
      )}
    </div>
  );
};

/* ───────────────────────────── Hero ───────────────────────────── */
const PeopleHero = ({ stats }) => (
  <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0a0a14] via-[#0d0b1f] to-[#0a0a14] p-6 md:p-7" data-testid="people-hero">
    <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] bg-cyan-500/[0.10] rounded-full blur-[100px]" />
    <div className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-violet-500/[0.08] rounded-full blur-[100px]" />
    <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
      <div className="flex items-start gap-4">
        <div className="relative w-14 h-14 flex-shrink-0">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/30" />
          <div className="absolute inset-0 rounded-2xl flex items-center justify-center">
            <Users className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-1">Administration · People</p>
          <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight">
            People <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">Hub</span>
          </h1>
          <p className="text-white/50 text-sm mt-1">Everyone in the org — staff, guests and community members in one place.</p>
        </div>
      </div>
      {stats && (stats.staff > 0 || stats.users > 0) && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/[0.10] border border-emerald-500/25">
            <span className="relative flex items-center justify-center">
              <span className="absolute w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-emerald-300 text-xs font-semibold">{stats.online} active now</span>
          </div>
          {stats.celebrations > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/[0.10] border border-amber-500/25">
              <PartyPopper className="w-3 h-3 text-amber-300" />
              <span className="text-amber-300 text-xs font-semibold">{stats.celebrations} celebrating</span>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

/* ──────────────────────────── Overview ───────────────────────────── */
const PeopleOverview = ({ stats, recentActivity, onJumpTo, onOpenStaff }) => {
  const kpis = [
    { id: 'staff',       label: 'Staff',          value: stats.staff,         hex: '#00E5FF', Icon: Users,     jump: 'staff' },
    { id: 'users',       label: 'User accounts',  value: stats.users,         hex: '#8A2BE2', Icon: UserCheck, jump: 'users' },
    { id: 'online',      label: 'Online now',     value: stats.online,        hex: '#10B981', Icon: Activity },
    { id: 'celeb',       label: 'This week',      value: stats.celebrations,  hex: '#F59E0B', Icon: PartyPopper, sub: 'Celebrations' },
    { id: 'suspended',   label: 'Suspended',      value: stats.suspended,     hex: '#F43F5E', Icon: UserX,     jump: stats.suspended > 0 ? 'staff' : null },
  ];

  const quickActions = [
    { id: 'add-staff',    label: 'Add staff',        desc: 'Onboard a new team member',          hex: '#00E5FF', Icon: UserPlus, jump: 'staff' },
    { id: 'hr-notice',    label: 'Compose HR notice',desc: 'Warning, meeting, or kudos',         hex: '#F43F5E', Icon: Shield,   href: '/staff/hr-inbox' },
    { id: 'inbox-blast',  label: 'Inbox blast',      desc: 'Send a message to many users',       hex: '#8A2BE2', Icon: Send,     jump: 'users' },
    { id: 'directory',    label: 'Open directory',   desc: 'Browsable staff profiles',           hex: '#10B981', Icon: BookUser, jump: 'directory' },
    { id: 'newsletter',   label: 'Send newsletter',  desc: 'Deliver to subscriber inboxes',      hex: '#F59E0B', Icon: Megaphone,href: '/staff/content-hub' },
    { id: 'export',       label: 'Export list',      desc: 'Download the staff directory',       hex: '#06B6D4', Icon: Download, action: 'export' },
  ];

  const triggerExport = () => {
    const h = getAuthHeaders(); if (!h) return;
    axios.get(`${BACKEND_URL}/api/admin/staff-accounts`, { headers: h }).then(res => {
      const rows = res.data || [];
      if (!rows.length) { toast.error('Nothing to export'); return; }
      const cols = ['username', 'display_name', 'email', 'role', 'department', 'job_title', 'date_of_birth', 'hire_date', 'is_suspended'];
      const csv = [cols.join(',')].concat(rows.map(r => cols.map(c => JSON.stringify(r[c] ?? '')).join(','))).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `staff-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} staff`);
    }).catch(() => toast.error('Export failed'));
  };

  const handleAction = (q) => {
    if (q.jump) onJumpTo(q.jump);
    else if (q.href) window.location.href = q.href;
    else if (q.action === 'export') triggerExport();
  };

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map(k => (
          <button key={k.id} onClick={() => k.jump && onJumpTo(k.jump)} disabled={!k.jump}
            data-testid={`overview-kpi-${k.id}`}
            className="group text-left relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 hover:border-white/[0.15] disabled:cursor-default transition-all">
            <div className="pointer-events-none absolute -top-8 -right-8 w-20 h-20 rounded-full blur-3xl opacity-30 group-hover:opacity-70 transition-opacity" style={{ background: k.hex }} />
            <div className="relative">
              <k.Icon className="w-4 h-4 mb-2" style={{ color: k.hex }} />
              <p className="text-3xl font-light text-white tabular-nums leading-none">{k.value}</p>
              <p className="text-[9px] tracking-[0.15em] uppercase text-white/40 mt-2">{k.sub || k.label}</p>
              {k.jump && <ArrowRight className="w-3 h-3 text-white/25 absolute top-0 right-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />}
            </div>
          </button>
        ))}
      </div>

      {/* Celebrations preview */}
      <CelebrationsPulse onOpenStaff={onOpenStaff} />

      {/* Quick actions */}
      <section>
        <div className="flex items-center gap-2 mb-2.5">
          <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-semibold">Quick actions</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {quickActions.map(q => (
            <button key={q.id} onClick={() => handleAction(q)}
              data-testid={`quick-action-${q.id}`}
              className="group text-left relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] p-4 transition-all">
              <div className="pointer-events-none absolute -top-16 -right-16 w-32 h-32 rounded-full blur-[60px] opacity-20 group-hover:opacity-50 transition-opacity" style={{ background: q.hex }} />
              <div className="relative flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0"
                  style={{ background: `${q.hex}18`, borderColor: `${q.hex}40`, boxShadow: `0 0 20px -10px ${q.hex}` }}>
                  <q.Icon className="w-4 h-4" style={{ color: q.hex }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[13.5px] font-semibold leading-tight">{q.label}</p>
                  <p className="text-white/50 text-[11px] mt-0.5">{q.desc}</p>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-white/25 group-hover:text-white/70 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Recent admin activity (own) */}
      <section>
        <div className="flex items-center gap-2 mb-2.5">
          <History className="w-3.5 h-3.5 text-violet-300" />
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-semibold">Your recent admin activity</p>
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-2">
          {recentActivity.length === 0 ? (
            <p className="text-white/35 text-xs italic text-center py-6">No recent actions recorded.</p>
          ) : recentActivity.slice(0, 6).map((e, i) => (
            <div key={e.id || i} className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-white/[0.03] transition-all" data-testid={`overview-activity-${i}`}>
              <div className="w-7 h-7 rounded-lg bg-violet-500/[0.10] border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                <Activity className="w-3 h-3 text-violet-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/85 text-[12.5px] font-medium">
                  {(e.action || 'event').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </p>
                <div className="flex items-center gap-2 text-[10.5px] text-white/40 mt-0.5">
                  <Clock className="w-2.5 h-2.5" />
                  <span>{e.timestamp ? new Date(e.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                  {e.actor && e.actor !== 'system' && <><span>·</span><span>by {e.actor}</span></>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PeopleHubPage;
